import { useMemo } from 'react'
import { ColDef, ColGroupDef } from 'ag-grid-community'
import { intl } from '../../../../../i18n'
import { PermissionPolicyOption } from '../../../../../domain/value-object/PermissionPolicy'
import { ApplicationFunctionPermissionRow } from '../../types'
import {
  Operation,
  OperationI18nLabelKey,
  scopeVoService,
} from '../../../../../domain/value-object/ScopeVO'
import { useRowNumber } from '../../../../page-properties/bulksheet-properties/rowNumber'
import { GRAYED_OUT_STYLE } from '../../../../containers/commons/AgGrid/cellStyle'

const POLICY_TYPE_I18N_KEY: Record<string, string> = {
  TENANT: 'permissionPolicies.columnGroup.tenantPolicies',
  PROJECT: 'permissionPolicies.columnGroup.projectPolicies',
}

const createPolicyColumn = (
  policy: PermissionPolicyOption
): ColDef<ApplicationFunctionPermissionRow> => ({
  field: `scopeByPermissionPolicyUuid.${policy.uuid}`,
  headerName: policy.name,
  headerTooltip: policy.description,
  width: 150,
  valueGetter: params => {
    if (policy.scopes.some(s => scopeVoService.areAvailableAllFunctions(s))) {
      return intl.formatMessage({
        id: OperationI18nLabelKey[Operation.ALL],
      })
    }
    const scope = params.data?.scopeByPermissionPolicyUuid?.[policy.uuid]
    if (!scope) {
      return ''
    }
    const i18nKey = OperationI18nLabelKey[scope.operation]
    return i18nKey ? intl.formatMessage({ id: i18nKey }) : ''
  },
  cellStyle: params => {
    const hasPermission = !!params.value
    return hasPermission ? undefined : GRAYED_OUT_STYLE
  },
  filter: 'agSetColumnFilter',
  floatingFilter: true,
  editable: false,
})

const createPolicyColumnGroups = (
  permissionPolicyOptions: PermissionPolicyOption[]
): ColGroupDef<ApplicationFunctionPermissionRow>[] => {
  const groupedByType = new Map<string, PermissionPolicyOption[]>()
  permissionPolicyOptions.forEach(policy => {
    const existing = groupedByType.get(policy.type) ?? []
    existing.push(policy)
    groupedByType.set(policy.type, existing)
  })

  return Array.from(groupedByType.entries()).map(([type, policies]) => ({
    headerName: intl.formatMessage({
      id:
        POLICY_TYPE_I18N_KEY[type] ??
        'permissionPolicies.columnGroup.permissionPolicies',
    }),
    children: policies.map(createPolicyColumn),
  }))
}

type UseColumnDefsProps = {
  permissionPolicyOptions: PermissionPolicyOption[]
}

export const useColumnDefs = ({
  permissionPolicyOptions,
}: UseColumnDefsProps): (
  | ColDef<ApplicationFunctionPermissionRow>
  | ColGroupDef<ApplicationFunctionPermissionRow>
)[] => {
  const rowNumberCol = useRowNumber()

  const columnDefs = useMemo(() => {
    const cols: (
      | ColDef<ApplicationFunctionPermissionRow>
      | ColGroupDef<ApplicationFunctionPermissionRow>
    )[] = [
      rowNumberCol,
      {
        headerName: intl.formatMessage({
          id: 'permissionPolicies.columnGroup.screenInfo',
        }),
        children: [
          {
            field: 'directoryName',
            headerName: intl.formatMessage({
              id: 'permissionPolicies.column.directory',
            }),
            width: 250,
            pinned: 'left',
            floatingFilter: true,
            editable: false,
            filter: 'agSetColumnFilter',
            valueGetter: params => {
              return (
                params.data?.directoryName ||
                intl.formatMessage({ id: 'permissionPolicies.personalMenu' })
              )
            },
          },
          {
            field: 'applicationFunctionName',
            headerName: intl.formatMessage({
              id: 'permissionPolicies.column.applicationFunctionName',
            }),
            width: 250,
            pinned: 'left',
            floatingFilter: true,
            editable: false,
            filter: 'agSetColumnFilter',
          },
        ],
      },
      ...createPolicyColumnGroups(permissionPolicyOptions),
    ]

    return cols
  }, [permissionPolicyOptions, rowNumberCol])

  return columnDefs
}
